import { CLOCK_HEADER, type ImportResult, type SessionInfo, type TaskReview } from '@kolibri/shared';
import { currentLocale, translate } from '../i18n/i18n';
import { observeServerTime } from './clock';
import { authHeaders, clientHeaders, serverUrl, useSessionToken } from './server';

export class ApiError extends Error {
  status: number;
  code: string;

  constructor(status: number, message: string, code = 'error') {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export const isOffline = (err: unknown): boolean => err instanceof TypeError || (err instanceof ApiError && err.status === 0);

/**
 * The endpoints that hand back a session token, named rather than sniffed.
 *
 * A packaged app has to keep whatever the server issues, and the three that
 * issue one do not share a response shape: sign-in and sign-up return a
 * session, changing a password returns `{ ok: true }`. Looking for a `token`
 * field anywhere instead would have caught `/api/tokens`, whose `token` is an
 * API token for somebody else's script — storing that as the session would
 * swap this client's identity for one with different scopes.
 */
const ISSUES_SESSION = new Set(['/api/auth/login', '/api/auth/register', '/api/me/password']);

/**
 * A request that carries a file is not a clock reading.
 *
 * The offset is guessed from the middle of the round trip, so the guess is only
 * as good as the round trip is short — and half of a thirty-second upload is
 * not a measurement of anything. Deciding it from the body rather than from a
 * flag at the two call sites means the next thing that streams a blob is left
 * out by itself.
 */
const carriesFile = (body: BodyInit | null | undefined): boolean =>
  body instanceof Blob || body instanceof ArrayBuffer || ArrayBuffer.isView(body);

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  const sentAt = Date.now();
  try {
    response = await fetch(serverUrl(path), {
      credentials: 'include',
      ...init,
      headers: {
        ...(init.body && typeof init.body === 'string' ? { 'content-type': 'application/json' } : {}),
        // Both are empty in a browser, where the cookie above is the credential
        // and the page's own origin is the server. See `server.ts`.
        ...authHeaders(),
        ...clientHeaders(),
        ...init.headers,
      },
    });
  } catch (err) {
    throw new ApiError(0, err instanceof Error ? err.message : translate(currentLocale(), 'common.networkUnavailable'), 'offline');
  }

  // Here rather than after the body, and before the status is looked at. The
  // headers have arrived by now and the body may not have, so this is the
  // shortest round trip the reading can be taken over; and a 401 is as good a
  // reading as a 200, on a screen somebody reaches before signing in.
  if (!carriesFile(init.body)) {
    const stamp = Number(response.headers.get(CLOCK_HEADER));
    if (stamp) observeServerTime(stamp, sentAt, Date.now());
  }

  const text = await response.text();
  const payload = text ? safeParse(text) : null;
  if (!response.ok) {
    throw new ApiError(response.status, payload?.message ?? response.statusText, payload?.error ?? 'error');
  }
  if (ISSUES_SESSION.has(path) && typeof payload?.token === 'string') useSessionToken(payload.token);
  return payload as T;
}

const safeParse = (text: string): any => {
  try {
    return JSON.parse(text);
  } catch {
    return { message: text.slice(0, 200) };
  }
};

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, json(body ?? {})),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  config: () => request<{
    allowSignup: boolean; hasUsers: boolean; maxUploadBytes: number; version: string;
    sso: { label: string; only: boolean } | null;
    /** Null when no model is configured on this server. */
    ai: { provider: string } | null;
  }>('/api/config'),
  session: () => request<SessionInfo>('/api/session'),
  login: (email: string, password: string) => request<SessionInfo>('/api/auth/login', json({ email, password })),
  register: (body: { email: string; name: string; password: string; workspace?: string; invite?: string }) =>
    // The language goes with the sign-up so the starter project's workflow,
    // labels and templates are seeded in it rather than in English.
    request<SessionInfo>('/api/auth/register', json({ ...body, locale: currentLocale() })),
  logout: async () => {
    // Ordered so a failed sign-out does not leave a client holding a token the
    // server has already forgotten: the server first, then this side.
    const done = await request<{ ok: boolean }>('/api/auth/logout', json({}));
    useSessionToken(null);
    return done;
  },

  /**
   * Uploads go up as a raw body with the filename in a header — no multipart
   * boilerplate, and the browser can stream a large file straight through.
   */
  upload: async (
    workspaceId: string,
    file: Blob,
    name: string,
    /**
     * What to hang it on, already in the endpoint's own spelling.
     *
     * A flat bag of query parameters rather than a subject, because that is all
     * this function does with it — and because `uploadLink` in `kernel/files`
     * is the one place that knows a task is `task_id` and a decision is
     * `subject` plus `subject_id`. Two places knowing it is how they come to
     * disagree.
     */
    link?: Record<string, string | undefined>,
  ) => {
    const query = new URLSearchParams(Object.entries(link ?? {}).filter(([, v]) => !!v) as [string, string][]);
    return request<{ url: string; hash: string; name: string; mime: string; size: number; width?: number; height?: number; attachment?: any }>(
      `/api/workspaces/${workspaceId}/files?${query}`,
      {
        method: 'POST',
        body: file,
        headers: { 'content-type': file.type || 'application/octet-stream', 'x-filename': encodeURIComponent(name) },
      },
    );
  },

  /**
   * An export archive, up as a raw body — the same shape an upload takes, and
   * for the same reason: the file is already one blob and multipart would only
   * wrap it in a second copy of itself.
   */
  postArchive: <T>(path: string, archive: ArrayBuffer) =>
    request<T>(path, { method: 'POST', body: archive, headers: { 'content-type': 'application/zip' } }),

  search: (workspaceId: string, query: string, kind?: string) =>
    request<{ results: { kind: string; id: string; title: string; snippet: string; project_id: string | null }[] }>(
      `/api/workspaces/${workspaceId}/search?q=${encodeURIComponent(query)}${kind ? `&kind=${kind}` : ''}`,
    ),

  activity: (taskId: string) => request<any[]>(`/api/tasks/${taskId}/activity`),
  /** The same trail for a page: renames, moves, archiving, labels, visibility. */
  pageActivity: (pageId: string) => request<any[]>(`/api/pages/${pageId}/activity`),
  /**
   * Ask a model to read a task back.
   *
   * Slower than everything else in here — a model takes seconds, not
   * milliseconds — and nothing is written by it: what comes back is a
   * suggestion the person decides about. See `docs/ai.md`.
   */
  review: (taskId: string) => request<TaskReview>(`/api/tasks/${taskId}/review`, json({})),
  /** A board drawn — or redrawn — by the configured model. Nothing is written; see `ai-review/board.ts`. */
  draftBoard: (pageId: string, body: { instruction: string; board?: string; w?: number; h?: number; match?: boolean }) =>
    request<{ title: string; html: string; model: string }>(`/api/pages/${pageId}/boards/draft`, json(body)),
  import: (workspaceId: string, body: {
    csv: string; project_id: string; mapping: Record<string, string>; delimiter?: string; dry_run: boolean;
  }) => request<ImportResult>(`/api/workspaces/${workspaceId}/import`, json(body)),
  members: (workspaceId: string) => request<any[]>(`/api/workspaces/${workspaceId}/members`),
  tokens: () => request<any[]>('/api/tokens'),
  calendar: () => request<{ url: string | null }>('/api/me/calendar'),
  calendarOn: () => request<{ url: string }>('/api/me/calendar', json({})),
  calendarRotate: () => request<{ url: string }>('/api/me/calendar/rotate', json({})),
  calendarOff: () => request<{ url: null }>('/api/me/calendar', { method: 'DELETE' }),
  sessions: () => request<any[]>('/api/sessions'),
  revokeSession: (id: string) => request<{ ok: boolean }>(`/api/sessions/${id}`, { method: 'DELETE' }),
  audit: (workspaceId: string, before?: number) =>
    request<{ entries: any[]; oldest: number | null }>(
      `/api/workspaces/${workspaceId}/audit${before ? `?before=${before}` : ''}`,
    ),
  startTwoFactor: () => request<{ secret: string; uri: string }>('/api/me/2fa', json({})),
  confirmTwoFactor: (code: string) => request<{ recovery_codes: string[] }>('/api/me/2fa/confirm', json({ code })),
  disableTwoFactor: (password: string) => request<{ ok: boolean }>('/api/me/2fa/off', json({ password })),
  createToken: (body: { name: string; workspaceId?: string; scopes?: string; confined?: boolean }) =>
    request<{ token: string; id: string }>('/api/tokens', json(body)),
  revokeToken: (id: string) => request<{ ok: boolean }>(`/api/tokens/${id}`, { method: 'DELETE' }),
  invites: (workspaceId: string) => request<any[]>(`/api/workspaces/${workspaceId}/invites`),
  createInvite: (workspaceId: string, role: string) => request<{ code: string; url: string }>(`/api/workspaces/${workspaceId}/invites`, json({ role })),
  acceptInvite: (code: string) => request<{ workspaceId: string; session: SessionInfo }>(`/api/invites/${code}/accept`, json({})),
  automationRuns: (id: string) => request<any[]>(`/api/automations/${id}/runs`),
  applyTemplate: (id: string, body: { project_id?: string; assignees?: string[] }) =>
    request<any>(`/api/templates/${id}/apply`, json(body)),
  pageVersions: (pageId: string) => request<any[]>(`/api/pages/${pageId}/versions`),
  pageVersion: (pageId: string, versionId: string) => request<any>(`/api/pages/${pageId}/versions/${versionId}`),
  restoreVersion: (pageId: string, versionId: string) => request<any>(`/api/pages/${pageId}/versions`, json({ restore: versionId })),
  boardVersions: (boardId: string) => request<any[]>(`/api/artboards/${boardId}/versions`),
  boardVersion: (boardId: string, versionId: string) => request<any>(`/api/artboards/${boardId}/versions/${versionId}`),
  restoreBoardVersion: (boardId: string, versionId: string) =>
    request<any>(`/api/artboards/${boardId}/versions`, json({ restore: versionId })),

  /*
   * The three calls a value takes, and the reason they are here rather than in
   * the store: a secret's value is the one thing in this application that is
   * never mirrored. The store holds rows; this holds requests.
   */
  /*
   * Creating one is a server call rather than an optimistic write, and it is
   * the only entity here for which that is true. A value has to be sealed with
   * a key the browser does not have, so a secret cannot be born offline — and
   * the version that created the row locally and then asked the server to seal
   * a value for it asked about a row the server had never seen.
   */
  createSecret: (workspaceId: string, body: Record<string, unknown>) =>
    request<{ id: string; preview: string }>(`/api/workspaces/${workspaceId}/secrets`, json(body)),
  secretValue: (id: string, value: string) =>
    request<{ preview: string; rotated_at: number }>(`/api/secrets/${id}/value`, { method: 'PUT', body: JSON.stringify({ value }) }),
  revealSecret: (id: string) => request<{ value: string }>(`/api/secrets/${id}/reveal`, json({})),
  secretHistory: (id: string) =>
    request<{ entries: { id: string; verb: string; actor_name: string | null; created_at: number }[] }>(`/api/secrets/${id}/history`),
};
